import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Building2, Check, Search, Shield, UserPlus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import organizationService, { normalizeOrganizationName } from '../../services/organizationService';

const TYPES = ['Business', 'Institution', 'Non-Profit'];

const statusClass = (status) => ({
  approved: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-rose-100 text-rose-700',
  revoked: 'bg-slate-200 text-slate-600',
  withdrawn: 'bg-slate-200 text-slate-600'
}[status] || 'bg-slate-100 text-slate-600');

const OrganizationMembershipManager = ({ showAdminTools = true }) => {
  const { user, userProfile, refreshUserProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [memberships, setMemberships] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [managedMembers, setManagedMembers] = useState({});
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [draft, setDraft] = useState({
    type: userProfile?.profile?.organizationType || '',
    institutionType: userProfile?.profile?.institutionType || '',
    organizationEmail: userProfile?.profile?.organizationEmail || '',
    position: userProfile?.profile?.position || '',
    website: userProfile?.profile?.website || '',
    location: userProfile?.profile?.location || ''
  });

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const nextMemberships = await organizationService.getUserMemberships(user.uid);
      const nextOrganizations = await organizationService.getOrganizationsByIds(
        nextMemberships.map((item) => item.organizationId)
      );
      setMemberships(nextMemberships);
      setOrganizations(nextOrganizations);

      if (showAdminTools) {
        const adminMemberships = nextMemberships.filter((item) => item.status === 'approved' && item.role === 'admin');
        const entries = await Promise.all(adminMemberships.map(async (membership) => ([
          membership.organizationId,
          await organizationService.getOrganizationMemberships(membership.organizationId)
        ])));
        setManagedMembers(Object.fromEntries(entries));
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to load organisation memberships.');
    } finally {
      setLoading(false);
    }
  }, [showAdminTools, user?.uid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (normalizeOrganizationName(searchText).length < 2) {
        setSearchResults([]);
        return;
      }
      try {
        setSearchResults(await organizationService.searchOrganizations(searchText));
      } catch (error) {
        console.warn('Organisation search failed:', error);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchText]);

  const organizationById = useMemo(() => new Map(organizations.map((item) => [item.id, item])), [organizations]);
  const exactMatch = searchResults.find((item) => normalizeOrganizationName(item.name) === normalizeOrganizationName(searchText));

  const perform = async (key, action, successMessage) => {
    setBusy(key);
    try {
      await action();
      await refreshUserProfile?.();
      await load();
      toast.success(successMessage);
    } catch (error) {
      toast.error(error?.message || 'Organisation action failed.');
    } finally {
      setBusy('');
    }
  };

  const submitRequest = () => {
    const organizationEmail = draft.organizationEmail.trim();
    if (!organizationEmail) {
      toast.error('Organisation email is required.');
      return;
    }
    if (selectedOrganization) {
      perform('submit', () => organizationService.requestMembership({
        organizationId: selectedOrganization.id,
        organizationEmail,
        position: draft.position
      }), 'Membership request submitted.');
      return;
    }
    if (!searchText.trim() || !draft.type) {
      toast.error('Organisation name and type are required.');
      return;
    }
    perform('submit', () => organizationService.createOrganizationRequest({
      name: searchText,
      type: draft.type,
      institutionType: draft.institutionType,
      organizationEmail,
      position: draft.position,
      website: draft.website,
      location: draft.location
    }), 'Organisation submitted for platform approval.');
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Building2 className="h-5 w-5 text-[#008080]" /> Organisations
            </h3>
            <p className="mt-1 text-sm text-slate-500">Join approved organisations or request a new one without changing sheet ownership.</p>
          </div>
          {loading && <span className="text-xs text-slate-400">Loading...</span>}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {memberships.map((membership) => {
            const organization = organizationById.get(membership.organizationId);
            const isActive = userProfile?.activeOrganizationId === membership.organizationId;
            return (
              <article key={membership.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{organization?.name || 'Organisation request'}</p>
                    <p className="mt-1 text-xs text-slate-500">{membership.organizationEmail}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${statusClass(membership.status)}`}>
                    {membership.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-600">{membership.role}</span>
                  {membership.status === 'approved' && (
                    <button
                      type="button"
                      disabled={isActive || busy === `active_${membership.id}`}
                      onClick={() => perform(`active_${membership.id}`, () => organizationService.setActiveOrganization(membership.organizationId), 'Active organisation updated.')}
                      className="rounded-full bg-[#008080] px-3 py-1 text-xs font-semibold text-white disabled:bg-slate-300"
                    >
                      {isActive ? 'Active' : 'Set active'}
                    </button>
                  )}
                  {membership.role !== 'admin' && !['rejected', 'revoked', 'withdrawn'].includes(membership.status) && (
                    <button
                      type="button"
                      onClick={() => perform(`leave_${membership.id}`, () => organizationService.leaveOrganization(membership.organizationId), 'Organisation membership withdrawn.')}
                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600"
                    >
                      Leave
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
          <label className="block text-sm font-semibold text-slate-700">Search organisation by name</label>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input
              value={searchText}
              onChange={(event) => { setSearchText(event.target.value); setSelectedOrganization(null); }}
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#008080]"
              placeholder="Start typing an organisation name"
            />
          </div>
          {searchResults.length > 0 && !selectedOrganization && (
            <div className="mt-2 space-y-1 rounded-lg border border-slate-200 bg-white p-2">
              {searchResults.map((organization) => (
                <button
                  key={organization.id}
                  type="button"
                  onClick={() => { setSelectedOrganization(organization); setSearchText(organization.name); }}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50"
                >
                  <span className="font-semibold text-slate-800">{organization.name}</span>
                  <span className="text-xs text-slate-400">{organization.type}</span>
                </button>
              ))}
            </div>
          )}
          {selectedOrganization && (
            <div className="mt-2 flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <span className="flex items-center gap-2"><Check className="h-4 w-4" /> Joining {selectedOrganization.name}</span>
              <button type="button" onClick={() => setSelectedOrganization(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          {!selectedOrganization && searchText.trim() && !exactMatch && (
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <select value={draft.type} onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm">
                <option value="">Organisation type</option>
                {TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
              {draft.type === 'Institution' && (
                <input value={draft.institutionType} onChange={(e) => setDraft((prev) => ({ ...prev, institutionType: e.target.value }))} placeholder="Institution type" className="rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
              )}
              <input value={draft.website} onChange={(e) => setDraft((prev) => ({ ...prev, website: e.target.value }))} placeholder="Website (optional)" className="rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
              <input value={draft.location} onChange={(e) => setDraft((prev) => ({ ...prev, location: e.target.value }))} placeholder="Location (optional)" className="rounded-lg border border-slate-200 px-3 py-2.5 text-sm" />
            </div>
          )}
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <input type="email" value={draft.organizationEmail} onChange={(e) => setDraft((prev) => ({ ...prev, organizationEmail: e.target.value }))} placeholder="Your organisation email" className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm" />
            <input value={draft.position} onChange={(e) => setDraft((prev) => ({ ...prev, position: e.target.value }))} placeholder="Position or title" className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm" />
          </div>
          <button type="button" disabled={busy === 'submit' || !searchText.trim()} onClick={submitRequest} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#008080] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            <UserPlus className="h-4 w-4" />
            {selectedOrganization ? 'Request membership' : 'Create organisation request'}
          </button>
        </div>
      </div>

      {showAdminTools && Object.entries(managedMembers).map(([organizationId, members]) => {
        const organization = organizationById.get(organizationId);
        return (
          <section key={`manage_${organizationId}`} className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Shield className="h-5 w-5 text-[#008080]" /> Manage {organization?.name || 'organisation'}</h3>
            <div className="mt-3 space-y-2">
              {members.map((member) => (
                <div key={member.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-3 text-sm">
                  <div><p className="font-semibold text-slate-800">{member.organizationEmail || member.userId}</p><p className="text-xs text-slate-500">{member.position || member.role}</p></div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${statusClass(member.status)}`}>{member.status}</span>
                    {member.status === 'pending' && <>
                      <button type="button" onClick={() => perform(`approve_${member.id}`, () => organizationService.reviewMembership(member.id, 'approved'), 'Membership approved.')} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">Approve</button>
                      <button type="button" onClick={() => perform(`reject_${member.id}`, () => organizationService.reviewMembership(member.id, 'rejected'), 'Membership rejected.')} className="rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-700">Reject</button>
                    </>}
                    {member.status === 'approved' && member.userId !== user?.uid && <>
                      <button type="button" onClick={() => perform(`role_${member.id}`, () => organizationService.setMemberRole(member.id, member.role === 'admin' ? 'member' : 'admin'), 'Member role updated.')} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700">Make {member.role === 'admin' ? 'member' : 'admin'}</button>
                      <button type="button" onClick={() => perform(`revoke_${member.id}`, () => organizationService.reviewMembership(member.id, 'revoked'), 'Membership revoked.')} className="rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-700">Revoke</button>
                    </>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default OrganizationMembershipManager;
